import { useEffect, useState } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import { MAP_STYLE_URL, MAP_TILE_URL, MAP_ATTRIBUTION } from '../constants/colors';

/**
 * Подложка карты: векторные тайлы OpenFreeMap, стиль Positron.
 *
 * Почему не растр. Растровые тайлы OSM отдаются только в 256px без ретины —
 * на телефоне они мылят, — и несут всю разметку разом: вывески магазинов,
 * значки POI, подписи на каждом углу. На таком фоне теряется то, ради чего
 * карта и открыта: линия маршрута и точки. Positron рисует минимум —
 * улицы, воду, кварталы, — и векторно, то есть резко на любом экране.
 *
 * Слой добавляется внутрь существующей карты Leaflet, поэтому маркеры,
 * линия маршрута и слежение за пользователем продолжают работать как были.
 *
 * maplibre грузится динамически: это ~200 КБ, и они не нужны тем, кто
 * прогулку не открывал. Пока модуль едет — и если не доехал — показываем
 * растровый OSM: карта без подложки бесполезна, а OpenFreeMap бесплатный
 * и ничего не обещает по доступности.
 */
const VectorTiles = () => {
  const map = useMap();
  const [fallback, setFallback] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let layer = null;

    (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default;
        await import('maplibre-gl/dist/maplibre-gl.css');
        await import('@maplibre/maplibre-gl-leaflet');
        if (cancelled) return;

        // Мост вешает L.maplibreGL на глобальный Leaflet
        const L = (await import('leaflet')).default;
        if (cancelled || typeof L.maplibreGL !== 'function') {
          setFallback(true);
          return;
        }

        layer = L.maplibreGL({
          style: MAP_STYLE_URL,
          attribution: MAP_ATTRIBUTION,
          // Ярлыки Leaflet рисует поверх; интерактив внутри GL нам не нужен
          interactive: false,
          maplibreGL: maplibregl,
        });

        layer.on?.('error', () => setFallback(true));
        layer.addTo(map);
        setReady(true);

        // Подписи только по-русски. Positron по умолчанию пишет два языка
        // («Kamennoostrovsky Prospekt / Каменноостровский проспект») —
        // вдвое больше текста на карте, где и так тесно
        const gl = layer.getMaplibreMap?.();
        if (gl) {
          const russianLabels = () => {
            gl.getStyle()?.layers?.forEach((l) => {
              if (l.type !== 'symbol') return;
              const field = gl.getLayoutProperty(l.id, 'text-field');
              // Номера домов и прочее без названий не трогаем
              if (!field || !JSON.stringify(field).includes('name')) return;
              gl.setLayoutProperty(l.id, 'text-field', ['coalesce', ['get', 'name:ru'], ['get', 'name']]);
            });
          };
          if (gl.isStyleLoaded()) russianLabels();
          else gl.once('load', russianLabels);
        }
      } catch {
        // Нет WebGL, заблокирован CDN, не собрался чанк — не важно:
        // пользователю нужна карта, а не разбор причины
        if (!cancelled) setFallback(true);
      }
    })();

    return () => {
      cancelled = true;
      if (layer && map.hasLayer?.(layer)) map.removeLayer(layer);
    };
  }, [map]);

  // Растр держим под векторным слоем, пока тот не встал: иначе на медленной
  // сети человек несколько секунд смотрит в пустой серый прямоугольник
  if (ready && !fallback) return null;

  return (
    <TileLayer
      url={MAP_TILE_URL}
      attribution={MAP_ATTRIBUTION}
      className="map-tiles-muted"
      maxZoom={19}
    />
  );
};

export default VectorTiles;
